import type { LegacyHandoff } from './types';

/** The keys changeplan-frontend's loginCallback writes and logoutCallback removes. */
export const LEGACY_STORAGE_KEYS = [
  'accessToken',
  'refreshToken',
  'Meteor.userId',
  'Meteor.loginToken',
  'Meteor.region',
] as const;

export const LEGACY_LANDING = '/initiatives/list';

/**
 * Byte-for-byte what Angular writes: its LocalStorageService JSON-stringifies
 * (so the two Cognito tokens are stored quoted), while the Meteor.* setters
 * call localStorage.setItem directly with the raw string.
 */
export function writeLegacySession(h: LegacyHandoff, storage: Storage = localStorage): void {
  // All five or nothing: an empty value would leave Angular with a half session.
  const values = [h.accessToken, h.refreshToken, h.userId, h.legacyToken, h.region];
  if (values.some((v) => typeof v !== 'string' || v.length === 0)) {
    throw new Error('Incomplete legacy handoff');
  }
  storage.setItem('accessToken', JSON.stringify(h.accessToken));
  storage.setItem('refreshToken', JSON.stringify(h.refreshToken));
  storage.setItem('Meteor.userId', h.userId);
  storage.setItem('Meteor.loginToken', h.legacyToken);
  storage.setItem('Meteor.region', h.region);
}

/** Frontier's own surfaces (and its assets), which a legacy user must never be sent to. */
const NEVER_AFTER_HANDOFF = [
  '/login',
  '/logout',
  '/auth',
  '/web-api',
  '/web-static',
  '/_next',
  '/api',
  '/sso',
];

function isSafeNext(next: string | null): next is string {
  if (!next || !next.startsWith('/')) return false;
  if (next.startsWith('//') || next.startsWith('/\\')) return false;
  // Browsers strip tab/newline inside URLs, so `/\t/evil.example` becomes `//evil.example`.
  if (/[\u0000-\u001f\u007f]/.test(next)) return false;
  // Judge the path the browser will actually request: URL resolves `..`, drops
  // the fragment and query, and turns backslashes into slashes, so `/logout#x`
  // and `/x/../logout` cannot dodge the deny-list.
  let url: URL;
  try {
    url = new URL(next, 'http://x');
  } catch {
    return false;
  }
  if (url.origin !== 'http://x') return false;
  let decoded: string;
  try {
    decoded = decodeURIComponent(url.pathname);
  } catch {
    return false;
  }
  return ![url.pathname, decoded].some((path) =>
    NEVER_AFTER_HANDOFF.some((p) => path === p || path.startsWith(`${p}/`)),
  );
}

/** A same-site path Angular or Meteor can serve, else the legacy home. */
export function resolveHandoffTarget(next: string | null): string {
  return isSafeNext(next) ? next : LEGACY_LANDING;
}

export function handoffUrl(next: string | null): string {
  return isSafeNext(next) ? `/auth/handoff?next=${encodeURIComponent(next)}` : '/auth/handoff';
}
